/**
 * Save manager identifiers
 */
export enum SaveManagerId {
  Local = 'local',
  Cloud = 'cloud',
}

/**
 * Interface for save managers (local, cloud, etc.)
 * Each save manager implements this contract to handle game save data
 */
export interface ISaveManager<TGameData = any> {
  /**
   * Unique identifier for this save manager
   */
  readonly id: SaveManagerId;

  /**
   * Determine if this save manager should be active/enabled
   * Examples:
   * - Local: always true
   * - Cloud: only when user is authenticated
   */
  isEnabled(): boolean;

  /**
   * Save game data
   * @param gameId The game identifier
   * @param playerId The player identifier
   * @param data The game data to save
   * @param timestamp The timestamp of the save
   * @param deviceId The unique identifier for the device making the save
   * @param expirationDate Optional expiration date for cleanup (local storage uses this, cloud ignores it)
   */
  save(gameId: string, playerId: string, data: TGameData, timestamp: number, deviceId: string, expirationDate?: Date): Promise<void>;

  /**
   * Load game data
   * @param gameId The game identifier
   * @param playerId The player identifier
   * @returns The saved game data and timestamp, or null if no save exists
   */
  load(gameId: string, playerId: string): Promise<SavedData<TGameData> | null>;

  /**
   * Priority for reconciliation (higher number = higher priority)
   * Used when multiple save managers have conflicting data
   */
  getPriority(): number;
}

/**
 * Saved data with metadata
 */
export interface SavedData<TGameData = any> {
  savedGameData: TGameData;
  timestamp: number;
  source: SaveManagerId; // Which save manager this came from
  deviceId?: string; // Device that created this save (for reconciliation)
}
